import React, { FC } from 'react';

import TicketsReadStatusSelect from '../TicketsActionDropDown/TicketsActionDropDown';
import useTicketsRefinement from '../TicketsRefinement/hooks/useTicketsRefinement';
import {
  ArrowDownIconStyled,
  ArrowUpIconStyled,
  HeaderAgeStyled,
  HeaderAmountStyled,
  HeaderApprovedStyled,
  HeaderAssigneeStyled,
  HeaderAttachmentStyled,
  HeaderCategoryStyled,
  HeaderClaimAgeStyled,
  HeaderClaimStatusStyled,
  HeaderLastCommentStyled,
  HeaderLineNumberStyled,
  HeaderPriorityStyled,
  HeaderROStyled,
  HeaderStatusStyled,
  HeaderTicketCheckBox,
  SortingIconsWrapStyled,
  TextTitleStyled,
  TitleButtonStyled,
} from './TicketsSorting.styled';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ParameterSortingValuesE, TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { Column } from 'declarations/types/common';

type TicketsSortingProps = {
  selectedTicketsIds: string[];
  columns: Column[];
};

const TicketsSorting: FC<TicketsSortingProps> = ({ selectedTicketsIds, columns }) => {
  const {
    isRONumberSortingActive,
    isLineNumberSortingActive,
    isAgeSortingActive,
    isClaimAgeSortingActive,
    isCloseROAgeSortingActive,
    isStatusSortingActive,
    isAssigneeSortingActive,
    isCategorySortingActive,
    isAdvisorSortingActive,
    isTechSortingActive,
    isAscSortingActive,
    isAmountSortingActive,
    isSubCategorySortingActive,
    isApprovedByDealerSortingActive,
    hasAttachmentSortingActive,
    isClaimStatusSortingActive,
    isTicketPrioritySortingActive,
    handleUpdateSortingClick,
  } = useTicketsRefinement();
  const { isMobile } = useResponsive();
  const storedVisibleColumnsJSON = localStorage.getItem('visibleColumns');
  const storedVisibleColumns = storedVisibleColumnsJSON
    ? JSON.parse(storedVisibleColumnsJSON)
    : columns;

  const isColumnVisible = (columnId: string) => {
    const storedColumn = storedVisibleColumns.find((col: Column) => col.id === columnId);
    return storedColumn ? storedColumn.visible : false;
  };
  return (
    <thead>
      <tr>
        <HeaderTicketCheckBox title="Action button">
          <TicketsReadStatusSelect selectedTicketsIds={selectedTicketsIds} />
        </HeaderTicketCheckBox>
        {storedVisibleColumns.map((column: Column) => (
          <React.Fragment key={column.id}>
            {isColumnVisible(column.id) && column.label === 'Priority' && (
              <HeaderPriorityStyled title="Sort by Priority">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.TicketPriority
                  )}
                  $isActive={isTicketPrioritySortingActive}
                >
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isTicketPrioritySortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isTicketPrioritySortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderPriorityStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'RO number' && (
              <HeaderROStyled title="Sort by RO">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.RONumber
                  )}
                  $isActive={isRONumberSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    RO
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isRONumberSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isRONumberSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderROStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Line number' && (
              <HeaderLineNumberStyled title="Sort by Line">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.LineNumber
                  )}
                  $isActive={isLineNumberSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    Line
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isLineNumberSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isLineNumberSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderLineNumberStyled>
            )}

            {isColumnVisible(column.id) && column.label === 'Age (days)' && (
              <HeaderAgeStyled title="Sort by Age">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.Age
                  )}
                  $isActive={isAgeSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    Age
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isAgeSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isAgeSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderAgeStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Claim Age (days)' && (
              <HeaderClaimAgeStyled title="Sort by Claim Age">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.ClaimAge
                  )}
                  $isActive={isClaimAgeSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body5' : 'body3'} align="left">
                    Claim Age
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isClaimAgeSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isClaimAgeSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderClaimAgeStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Closed RO Age' && (
              <HeaderClaimAgeStyled title="Sort by Closed RO Age">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.ClosedROAge
                  )}
                  $isActive={isCloseROAgeSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body5' : 'body3'} align="left">
                    Closed RO Age
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isCloseROAgeSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isCloseROAgeSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderClaimAgeStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Status' && (
              <HeaderStatusStyled title="Sort by Status">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.Status
                  )}
                  $isActive={isStatusSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    Status
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isStatusSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isStatusSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderStatusStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Assigned to:' && (
              <HeaderAssigneeStyled title="Sort by Assigned To">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.Assignee
                  )}
                  $isActive={isAssigneeSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    Assigned To
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isAssigneeSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isAssigneeSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderAssigneeStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Category' && (
              <HeaderCategoryStyled title="Sort by Category">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.RequestCategory
                  )}
                  $isActive={isCategorySortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    Category
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isCategorySortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isCategorySortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderCategoryStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Subcategory' && (
              <HeaderCategoryStyled title="Sort by Subcategory">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.SubCategory
                  )}
                  $isActive={isSubCategorySortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    Subcategory
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isSubCategorySortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isSubCategorySortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderCategoryStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Last Comment' && (
              <HeaderLastCommentStyled title="Sort by Last Comment">
                <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                  Last Comment
                </TextTitleStyled>
              </HeaderLastCommentStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Claim Status' && (
              <HeaderClaimStatusStyled title="Sort by Claim Status">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.ClaimStatus
                  )}
                  $isActive={isClaimStatusSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    Claim Status
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isClaimStatusSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isClaimStatusSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderClaimStatusStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Amount' && (
              <HeaderAmountStyled title="Sort by Amount">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.Amount
                  )}
                  $isActive={isAmountSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    Amount
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isAmountSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isAmountSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderAmountStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Advisor number' && (
              <HeaderLineNumberStyled title="Sort by Advisor Number">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.Advisor
                  )}
                  $isActive={isAdvisorSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    Advisor
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isAdvisorSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isAdvisorSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderLineNumberStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Tech number' && (
              <HeaderLineNumberStyled title="Sort by Tech Number">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.Tech
                  )}
                  $isActive={isTechSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    Tech
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isTechSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isTechSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderLineNumberStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Attachment' && (
              <HeaderAttachmentStyled title="Sort by Attachment">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.HasAttachment
                  )}
                  $isActive={hasAttachmentSortingActive}
                >
                  <TextTitleStyled variant={isMobile ? 'body4' : 'body3'} align="left">
                    Attachment
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={hasAttachmentSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={hasAttachmentSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderAttachmentStyled>
            )}
            {isColumnVisible(column.id) && column.label === 'Approved' && (
              <HeaderApprovedStyled title="Sort by Approved">
                <TitleButtonStyled
                  variant="text"
                  onClick={handleUpdateSortingClick(
                    TicketsRefinementKeysE.Parameter,
                    ParameterSortingValuesE.IsApprovedByDealer
                  )}
                  $isActive={isApprovedByDealerSortingActive}
                >
                  <TextTitleStyled
                    variant={isMobile ? 'body4' : 'body3'}
                    align={isMobile ? 'center' : 'left'}
                  >
                    {isMobile ? '✓' : 'Approved'}
                  </TextTitleStyled>
                  <SortingIconsWrapStyled className="sorting">
                    <ArrowUpIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isApprovedByDealerSortingActive}
                    />
                    <ArrowDownIconStyled
                      $isOrderAsc={isAscSortingActive}
                      $isActive={isApprovedByDealerSortingActive}
                    />
                  </SortingIconsWrapStyled>
                </TitleButtonStyled>
              </HeaderApprovedStyled>
            )}
          </React.Fragment>
        ))}
      </tr>
    </thead>
  );
};

export default TicketsSorting;
